import React, { useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/pages/CheckoutPage.css";
import "../styles/shared/LoadingState.css";
import { toast } from "../utils/toast";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { useProcessing } from "../contexts/ProcessingContext";
import { checkout } from "../services/orderService";
import {
  checkPincodeServiceability,
  fetchAddresses,
  fetchCheckoutCoupons,
  fetchWallet,
  fetchWalletQuote,
  fetchWalletSettings,
} from "../services/accountService";
import {
  Order,
  PincodeServiceabilityResult,
  PaymentMethod,
  UserAddress,
  WalletCoupon,
  WalletQuoteResponse,
  WalletSettings,
} from "../types/store";
import { formatCurrency } from "../utils/currency";
import { readSelectedAddress, storeSelectedAddress } from "../utils/selectedAddress";
import { MobilePageHeader } from "../components/layout/MobileAppChrome";
import useBodyScrollLock from "../hooks/useBodyScrollLock";

const DELIVERY_SLOTS = [
  "09:00-11:00",
  "11:00-13:00",
  "13:00-15:00",
  "15:00-18:00",
  "18:00-21:00",
];

const ORDER_SUCCESS_CONFETTI = [
  { left: "8%", top: "14%", delay: "0s", rotate: "-12deg" },
  { left: "18%", top: "22%", delay: "0.08s", rotate: "18deg" },
  { left: "30%", top: "10%", delay: "0.16s", rotate: "-22deg" },
  { left: "44%", top: "18%", delay: "0.04s", rotate: "12deg" },
  { left: "58%", top: "12%", delay: "0.12s", rotate: "-16deg" },
  { left: "70%", top: "24%", delay: "0.2s", rotate: "20deg" },
  { left: "82%", top: "16%", delay: "0.1s", rotate: "-8deg" },
  { left: "90%", top: "28%", delay: "0.18s", rotate: "14deg" },
] as const;

interface CheckoutFormState {
  recipientName: string;
  phone: string;
  addressLabel: "Home" | "Work";
  address: string;
  roadArea: string;
  landmark: string;
  stateName: string;
  city: string;
  postalCode: string;
  deliverySlot: string;
  priorityOrder: boolean;
  priorityNotes: string;
  addressSource: "SAVED_ADDRESS" | "MANUAL_ADDRESS" | "CURRENT_LOCATION" | "MAP_LOCATION";
  addressMapUrl: string;
}

interface CheckoutFieldErrors {
  contact?: string;
  deliverySlot?: string;
  stateName?: string;
  city?: string;
  postalCode?: string;
  address?: string;
}

const DEFAULT_PAYMENT_METHOD: PaymentMethod = "COD";

interface CheckoutPageProps {
  embedded?: boolean;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ embedded = false }) => {
  const { user } = useAuth();
  const { clearCart, subtotal } = useCart();
  const { startProcessing, stopProcessing } = useProcessing();
  const location = useLocation();
  const navigate = useNavigate();
  const [formState, setFormState] = useState<CheckoutFormState>({
    recipientName: user?.fullName || "",
    phone: user?.phoneNumber || "",
    addressLabel: "Home",
    address: "",
    roadArea: "",
    landmark: "",
    stateName: "",
    city: "",
    postalCode: "",
    deliverySlot: DELIVERY_SLOTS[0],
    priorityOrder: false,
    priorityNotes: "",
    addressSource: "MANUAL_ADDRESS",
    addressMapUrl: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [placedOrder, setPlacedOrder] = useState<Order | null>(null);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | "new">("new");
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [walletSettings, setWalletSettings] = useState<WalletSettings | null>(null);
  const [walletQuote, setWalletQuote] = useState<WalletQuoteResponse | null>(null);
  const [walletDetailsOpen, setWalletDetailsOpen] = useState(false);
  const [checkoutCoupons, setCheckoutCoupons] = useState<WalletCoupon[]>([]);
  const [couponPickerOpen, setCouponPickerOpen] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const paymentMethod = DEFAULT_PAYMENT_METHOD;
  const [useWalletBalance, setUseWalletBalance] = useState(false);
  const [walletApplyLoading, setWalletApplyLoading] = useState(false);
  const [, setLoadingAddresses] = useState(true);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderAnimationActive, setOrderAnimationActive] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState<PincodeServiceabilityResult | null>(null);
  const [deliveryWarningOpen, setDeliveryWarningOpen] = useState(false);
  const [deliveryWarningMessage, setDeliveryWarningMessage] = useState("");
  const [, setCheckingPincode] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<CheckoutFieldErrors>({});
  const orderAnimationTimeoutRef = useRef<number | null>(null);
  const placeOrderButtonRef = useRef<HTMLButtonElement | null>(null);
  const deliveryMode = user?.preferredDeliveryMode ?? "HOME_DELIVERY";
  const isStorePickup = deliveryMode === "STORE_PICKUP";

  useBodyScrollLock(Boolean(deliveryWarningOpen || walletDetailsOpen));

  useEffect(() => {
    return () => {
      if (orderAnimationTimeoutRef.current) {
        window.clearTimeout(orderAnimationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadAddresses = async () => {
      const processingId = startProcessing({
        title: "Loading checkout",
        message: "Bringing in your saved delivery addresses...",
      });
      try {
        const response = await fetchAddresses();
        setAddresses(response);
        const storedAddress = readSelectedAddress(user);
        const defaultAddress =
          (storedAddress?.id ? response.find((address) => address.id === storedAddress.id) : null) ??
          response.find((address) => address.defaultAddress) ??
          response[0];
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
          setFormState((current) => ({
            ...current,
            recipientName: defaultAddress.recipientName || user?.fullName || "",
            phone: defaultAddress.phone || user?.phoneNumber || "",
            addressLabel: (defaultAddress.label as CheckoutFormState["addressLabel"]) === "Work" ? "Work" : "Home",
            address: defaultAddress.streetAddress,
            roadArea: "",
            landmark: "",
            stateName: "",
            city: defaultAddress.city,
            postalCode: defaultAddress.postalCode,
            addressSource:
              (defaultAddress.addressSource as CheckoutFormState["addressSource"]) || "SAVED_ADDRESS",
            addressMapUrl: defaultAddress.addressMapUrl || "",
          }));
          storeSelectedAddress(user, defaultAddress);
        } else {
          setFormState((current) => ({
            ...current,
            recipientName: user?.fullName || "",
            phone: user?.phoneNumber || "",
            addressLabel: "Home",
            addressSource: "MANUAL_ADDRESS",
            addressMapUrl: "",
            stateName: "",
          }));
          storeSelectedAddress(user, null);
        }
      } catch {
        setAddresses([]);
        setSelectedAddressId("new");
        setFormState((current) => ({
          ...current,
          recipientName: user?.fullName || "",
          phone: user?.phoneNumber || "",
          addressLabel: "Home",
          addressSource: "MANUAL_ADDRESS",
          addressMapUrl: "",
          stateName: "",
        }));
        storeSelectedAddress(user, null);
      } finally {
        setLoadingAddresses(false);
        stopProcessing(processingId);
      }
    };

    if (!isStorePickup) {
      void loadAddresses();
      return;
    }

    setLoadingAddresses(false);
  }, [isStorePickup, startProcessing, stopProcessing, user]);

  useEffect(() => {
    const refreshStoredAddress = () => {
      const storedAddress = readSelectedAddress(user);
      if (!storedAddress?.id) {
        return;
      }
      setSelectedAddressId(storedAddress.id);
    };

    window.addEventListener("storage", refreshStoredAddress);
    window.addEventListener("eldoo:selected-address-updated", refreshStoredAddress as EventListener);
    return () => {
      window.removeEventListener("storage", refreshStoredAddress);
      window.removeEventListener("eldoo:selected-address-updated", refreshStoredAddress as EventListener);
    };
  }, [user]);

  useEffect(() => {
    const loadWalletMeta = async () => {
      try {
        const [walletSummary, settings] = await Promise.all([
          fetchWallet().catch(() => null),
          fetchWalletSettings().catch(() => null),
        ]);
        if (walletSummary) {
          setWalletBalance(walletSummary.balance);
        }
        if (settings) {
          setWalletSettings(settings);
        }
      } catch {
        setWalletSettings(null);
      }
    };

    void loadWalletMeta();
  }, []);

  useEffect(() => {
    const couponFromQuery = new URLSearchParams(location.search).get("coupon");
    if (couponFromQuery) {
      setAppliedCouponCode(couponFromQuery);
      setCouponCodeInput("");
      setSelectedCouponId(null);
      setCouponPickerOpen(false);
    }
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const loadCoupons = async () => {
      try {
        const coupons = await fetchCheckoutCoupons();
        if (!cancelled) {
          setCheckoutCoupons(coupons);
        }
      } catch {
        if (!cancelled) {
          setCheckoutCoupons([]);
        }
      }
    };

    void loadCoupons();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedAddress = useMemo(
    () => (typeof selectedAddressId === "number" ? addresses.find((address) => address.id === selectedAddressId) ?? null : null),
    [addresses, selectedAddressId],
  );
  const selectedCoupon = useMemo(
    () => checkoutCoupons.find((coupon) => coupon.id === selectedCouponId) ?? null,
    [checkoutCoupons, selectedCouponId],
  );
  const activeCouponCode = appliedCouponCode || selectedCoupon?.code || "";
  const activePostalCode = (selectedAddress?.postalCode ?? formState.postalCode).trim();
  const isWithinFreeDeliveryRadius = isStorePickup || Boolean(pincodeStatus?.serviceable);
  const shipping = isWithinFreeDeliveryRadius ? 0 : 100;
  const tax = isWithinFreeDeliveryRadius ? 0 : 25;
  const total = subtotal + shipping + tax;
  const couponPreviewDiscount = Math.max(
    0,
    subtotal + shipping + tax - (walletQuote?.orderTotal ?? total),
  );
  const waivedShippingAmount = 100;
  const waivedFeeAmount = 25;

  useEffect(() => {
    if (isStorePickup) {
      setPincodeStatus(null);
      setCheckingPincode(false);
      return;
    }

    if (!/^\d{6}$/.test(activePostalCode)) {
      setPincodeStatus(null);
      setCheckingPincode(false);
      return;
    }

    let cancelled = false;
    setCheckingPincode(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await checkPincodeServiceability(activePostalCode);
        if (!cancelled) {
          setPincodeStatus(response);
        }
      } catch {
        if (!cancelled) {
          setPincodeStatus(null);
        }
      } finally {
        if (!cancelled) {
          setCheckingPincode(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [activePostalCode, isStorePickup]);

  useEffect(() => {
    let cancelled = false;

    const loadWalletQuote = async () => {
      if (!walletSettings) {
        setWalletQuote(null);
        return;
      }

      try {
        const quote = await fetchWalletQuote({
          subtotal,
          discountAmount: 0,
          deliveryCharge: shipping,
          taxAmount: tax,
          deliveryMode,
          paymentMethod,
          useWalletBalance,
          walletAmountUsed: useWalletBalance ? undefined : 0,
          couponApplied: Boolean(activeCouponCode),
          couponCode: activeCouponCode || undefined,
        });
        if (!cancelled) {
          setWalletQuote(quote);
        }
      } catch {
        if (!cancelled) {
          setWalletQuote(null);
        }
      }
    };

    void loadWalletQuote();

    return () => {
      cancelled = true;
    };
  }, [activeCouponCode, deliveryMode, paymentMethod, shipping, subtotal, tax, useWalletBalance, walletSettings]);

  const extractCheckoutError = (error: unknown) => {
    if (error instanceof AxiosError) {
      const message = error.response?.data?.message;
      const isInternalMessage =
        typeof message === "string" &&
        /(transaction|unsaved-value|entity\.|hibernate|sql|database|optimistic locking)/i.test(
          message,
        );
      if (typeof message === "string" && message.trim() && !isInternalMessage) {
        return message;
      }
      const fieldErrors = error.response?.data?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        const firstMessage = Object.values(fieldErrors).find(
          (value) => typeof value === "string" && value.trim(),
        );
        if (typeof firstMessage === "string") {
          return firstMessage;
        }
      }
    }
    return "Couldn't place your order right now. Please try again.";
  };

  const closeDeliveryWarning = () => {
    setDeliveryWarningOpen(false);
  };

  const handleCouponCodeChange = (value: string) => {
    setCouponCodeInput(value);
    setCouponError("");
    if (value.trim()) {
      setSelectedCouponId(null);
    }
  };

  const applyCouponCode = () => {
    const code = couponCodeInput.trim();
    const matchingCoupon = checkoutCoupons.find(
      (coupon) => coupon.code.trim().toLowerCase() === code.toLowerCase(),
    );
    if (!matchingCoupon) {
      setAppliedCouponCode("");
      setCouponError("Please enter a valid coupon");
      return;
    }

    setCouponError("");
    setAppliedCouponCode(code);
    setCouponPickerOpen(false);
  };

  const handleSelectCoupon = (coupon: WalletCoupon | null) => {
    if (!coupon) {
      setSelectedCouponId(null);
      return;
    }
    setSelectedCouponId(coupon.id);
    setCouponCodeInput("");
    setCouponError("");
    setAppliedCouponCode(coupon.code);
    setCouponPickerOpen(false);
  };

  const clearCouponSelection = () => {
    setSelectedCouponId(null);
    setCouponCodeInput("");
    setCouponError("");
    setAppliedCouponCode("");
  };

  const handleUseWallet = async () => {
    if (walletApplyLoading || submittingOrder) {
      return;
    }

    setWalletApplyLoading(true);
    setUseWalletBalance(true);
    setWalletDetailsOpen(false);

    try {
      await Promise.resolve();
    } finally {
      setWalletApplyLoading(false);
    }
  };

  const handleDontUseWallet = () => {
    if (walletApplyLoading || submittingOrder) {
      return;
    }

    setUseWalletBalance(false);
    setWalletDetailsOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingOrder || orderAnimationActive) {
      return;
    }
    const nextErrors: CheckoutFieldErrors = {};
    const contactName = String(selectedAddress?.recipientName ?? formState.recipientName).trim();
    const contactPhone = String(selectedAddress?.phone ?? formState.phone).trim();
    if (!contactName || !contactPhone) {
      nextErrors.contact = "Please enter the recipient name and phone number.";
    }
    if (!isStorePickup) {
      if (!String(formState.deliverySlot).trim()) {
        nextErrors.deliverySlot = "Please choose a delivery slot.";
      }
      if (!String(selectedAddress?.city ?? formState.city).trim()) {
        nextErrors.city = "City is required.";
      }
      if (!String(selectedAddress?.postalCode ?? formState.postalCode).trim()) {
        nextErrors.postalCode = "Postal code is required.";
      }
      if (!String(selectedAddress?.streetAddress ?? formState.address).trim()) {
        nextErrors.address = "Address is required.";
      }
    }
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstMissingField = Object.values(nextErrors)[0];
      toast.warn(firstMissingField || "Please complete the required checkout details.");
      return;
    }

    if (!isStorePickup) {
      const serviceability = await checkPincodeServiceability(activePostalCode).catch(() => null);
      if (!serviceability?.serviceable) {
        setDeliveryWarningMessage(
          serviceability?.message || "Home delivery is not available for this pincode yet.",
        );
        setDeliveryWarningOpen(true);
        return;
      }
      setPincodeStatus(serviceability);
    }

    let nextAddressId = typeof selectedAddressId === "number" ? selectedAddressId : null;
    const requestedWalletAmount = useWalletBalance ? walletQuote?.walletAmountUsed ?? undefined : 0;
    const finalWalletQuote = useWalletBalance
      ? await fetchWalletQuote({
          subtotal,
          discountAmount: 0,
          deliveryCharge: shipping,
          taxAmount: tax,
          deliveryMode,
          paymentMethod,
          useWalletBalance,
          walletAmountUsed: requestedWalletAmount,
          couponApplied: Boolean(activeCouponCode),
          couponCode: activeCouponCode || undefined,
        }).catch(() => null)
      : null;
    if (useWalletBalance && !finalWalletQuote?.walletAllowed) {
      toast.error(finalWalletQuote?.reason || "Wallet cannot be used for this order.");
      return;
    }
    setSubmittingOrder(true);
    const processingId = startProcessing({
      title: "Placing your order",
      message: "Finalizing delivery details and order confirmation...",
    });
    try {
      const order = await checkout({
        deliveryMode,
        shippingName: selectedAddress?.recipientName ?? formState.recipientName ?? user?.fullName ?? "",
        email: user?.email || "",
        phone: selectedAddress?.phone ?? formState.phone ?? user?.phoneNumber ?? "",
        addressId: nextAddressId,
        shippingAddress: selectedAddress?.streetAddress ?? formState.address,
        addressSource: formState.addressSource,
        addressMapUrl: selectedAddress?.addressMapUrl || formState.addressMapUrl || undefined,
        city: selectedAddress?.city ?? formState.city,
        postalCode: selectedAddress?.postalCode ?? formState.postalCode,
        deliverySlot: isStorePickup ? "STORE_PICKUP_WINDOW" : formState.deliverySlot,
        priorityOrder: formState.priorityOrder,
        priorityNotes: formState.priorityNotes,
        paymentMethod,
        useWalletBalance,
        walletAmountUsed: useWalletBalance ? finalWalletQuote?.walletAmountUsed ?? 0 : 0,
        couponApplied: Boolean(activeCouponCode),
        couponCode: activeCouponCode || undefined,
      });
      setPlacedOrder(order);
      clearCart();
      setOrderAnimationActive(true);
      if (orderAnimationTimeoutRef.current) {
        window.clearTimeout(orderAnimationTimeoutRef.current);
      }
      orderAnimationTimeoutRef.current = window.setTimeout(() => {
        setSubmitted(true);
        setOrderAnimationActive(false);
        orderAnimationTimeoutRef.current = null;
      }, 1450);
    } catch (error) {
      toast.error(extractCheckoutError(error));
    } finally {
      setSubmittingOrder(false);
      stopProcessing(processingId);
    }
  };

  return (
    <section className={`shell section page-section checkout-page ${embedded ? "checkout-page--embedded" : ""}`}>
      {!embedded ? <MobilePageHeader title="Checkout" /> : null}
      <div className="section-heading">
        <div>
          <span className="eyebrow">Checkout</span>
          <h1>Review Order</h1>
        </div>
      </div>

      {submitted ? (
        <div className="store-card empty-state checkout-success-card is-visible">
          <div className="checkout-success-card__confetti" aria-hidden="true">
            {ORDER_SUCCESS_CONFETTI.map((piece, index) => (
              <span
                key={`${piece.left}-${piece.top}-${index}`}
                className="checkout-success-card__confetti-piece"
                style={
                  {
                    left: piece.left,
                    top: piece.top,
                    animationDelay: piece.delay,
                    transform: `rotate(${piece.rotate})`,
                  } as React.CSSProperties
                }
              />
            ))}
          </div>
          <div className="checkout-success-card__hero">
            <div className="checkout-success-card__icon" aria-hidden="true">
              ✓
            </div>
            <div className="checkout-success-card__badge">Order placed</div>
            <h2>Order placed successfully</h2>
            <p>
              Thanks {placedOrder?.shippingName}. Your order ID is #{placedOrder?.orderNumber || placedOrder?.id}.
            </p>
          </div>
          <div className="checkout-success-card__grid">
            <div>
              <span>Estimated delivery</span>
              <strong>{placedOrder?.deliverySlot || "Within 24-48 hours"}</strong>
            </div>
            <div>
              <span>Payment status</span>
              <strong>{placedOrder?.paymentStatus || "Pending"}</strong>
            </div>
            <div>
              <span>Delivery address</span>
              <strong>
                {placedOrder?.shippingAddress}
                {placedOrder?.city ? `, ${placedOrder.city}` : ""}
                {placedOrder?.postalCode ? ` ${placedOrder.postalCode}` : ""}
              </strong>
            </div>
            <div>
              <span>Payment method</span>
              <strong>Cash on delivery</strong>
            </div>
          </div>
          {placedOrder?.addressMapUrl ? (
            <a className="checkout-success-card__map" href={placedOrder.addressMapUrl} target="_blank" rel="noreferrer">
              Open delivery location
            </a>
          ) : null}
          <div className="checkout-success-actions">
            <Link className="button" to="/orders">
              View order page
            </Link>
            <Link className="button button--light" to="/">
              Continue shopping
            </Link>
          </div>
        </div>
      ) : (
        <div className="checkout-layout">
          <form className="store-card form-card checkout-form" onSubmit={handleSubmit}>
            <div className="checkout-form__body">
              <h2>{isStorePickup ? "Pickup Details" : "Delivery Details"}</h2>
              <div className="checkout-delivery-mode">
                <span>Selected fulfilment</span>
                <strong>{isStorePickup ? "Pick up at store" : "Home delivery"}</strong>
              </div>
              <div className="checkout-delivery-mode">
                <span>Order contact</span>
                <strong>{user?.fullName || "Profile name missing"} | {user?.phoneNumber || "Mobile missing"}</strong>
              </div>
              {fieldErrors.contact ? <p className="form-error checkout-form-error">{fieldErrors.contact}</p> : null}
              <div className="checkout-address-summary form-grid__wide checkout-section">
                <div className="checkout-address-summary__copy">
                  <span>Delivery address</span>
                  {selectedAddress ? (
                    <>
                      <strong>{selectedAddress.recipientName}</strong>
                      <p>{selectedAddress.streetAddress}</p>
                      <small>
                        {selectedAddress.city}, {selectedAddress.postalCode}
                      </small>
                      {selectedAddress.addressMapUrl ? (
                        <a href={selectedAddress.addressMapUrl} target="_blank" rel="noreferrer">
                          Open current address
                        </a>
                      ) : null}
                    </>
                  ) : (
                    <p>No address selected yet.</p>
                  )}
                </div>
                <Link
                  className="button checkout-address-summary__action"
                  to="/address"
                  state={{ returnTo: embedded ? "/cart?checkout=1" : "/checkout" }}
                >
                  Select address
                </Link>
              </div>
              <div className="checkout-coupon-picker form-grid__wide checkout-section checkout-section--coupon">
              <div className="checkout-coupon-picker__trigger">
                <div className="checkout-coupon-picker__trigger-left">
                  <span className="checkout-coupon-picker__icon" aria-hidden="true">🏷️</span>
                  <div className="checkout-coupon-picker__copy">
                    <strong>{activeCouponCode ? "Selected coupon" : "Coupons"}</strong>
                    <small>
                      {activeCouponCode ? activeCouponCode : "Pick one coupon or enter the code"}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="checkout-coupon-picker__action"
                  onClick={() => {
                    if (activeCouponCode) {
                      clearCouponSelection();
                      return;
                    }
                    if (!couponPickerOpen) {
                      navigate("/coupons", { state: { fromCheckout: true } });
                      return;
                    }
                    setCouponPickerOpen(false);
                  }}
                  disabled={submittingOrder}
                >
                  {activeCouponCode ? "Remove" : "Select coupon"}
                </button>
              </div>
              </div>
            <button
              type="button"
              className={`checkout-wallet-card form-grid__wide checkout-section checkout-section--wallet ${walletApplyLoading ? "is-applying" : ""}`}
              onClick={() => setWalletDetailsOpen(true)}
              disabled={walletApplyLoading || submittingOrder}
            >
              <div className="checkout-wallet-card__copy">
                <span className="checkout-wallet-card__eyebrow">Use wallet</span>
                <strong>
                  {walletApplyLoading ? (
                    <span className="button-loading checkout-wallet-card__loading">
                      <span className="button-loading__spinner" aria-hidden="true" />
                      Applying...
                    </span>
                  ) : (
                    formatCurrency(walletBalance)
                  )}
                </strong>
                <small>
                  {walletSettings
                    ? walletSettings.enableWalletUsage
                      ? "Wallet is available for this order"
                      : "Wallet usage is disabled by admin"
                    : "Loading wallet information"}
                </small>
              </div>
              <div className="checkout-wallet-card__meta">
                <span>Maximum usable</span>
                <strong>{formatCurrency(walletQuote?.maxWalletUsable ?? 0)}</strong>
              </div>
            </button>
            <div className="checkout-delivery-mode form-grid__wide">
              <span>Applied coupon</span>
              <strong>{activeCouponCode || "None selected"}</strong>
            </div>
            <div className="checkout-delivery-mode form-grid__wide">
              <span>Payment method</span>
              <strong>Cash on delivery</strong>
            </div>
            <section className="summary-card checkout-price-details">
              <h2>Price Details</h2>
              <div>
                <span>Total MRP</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              {activeCouponCode ? (
                <div className="summary-coupon-deduction">
                  <span>Coupon ({activeCouponCode})</span>
                  <strong>-{formatCurrency(couponPreviewDiscount)}</strong>
                </div>
              ) : null}
              <div>
                <span>Shipping</span>
                <strong>
                  {isStorePickup ? (
                    "Store pickup"
                  ) : isWithinFreeDeliveryRadius ? (
                    <span className="summary-waived-charge">
                      <s>{formatCurrency(waivedShippingAmount)}</s>
                      <span>Free</span>
                    </span>
                  ) : shipping === 0 ? (
                    "Free"
                  ) : (
                    formatCurrency(shipping)
                  )}
                </strong>
              </div>
              <div>
                <span>Platform Fee</span>
                <strong>
                  {isWithinFreeDeliveryRadius ? (
                    <span className="summary-waived-charge">
                      <s>{formatCurrency(waivedFeeAmount)}</s>
                      <span>Free</span>
                    </span>
                  ) : (
                    formatCurrency(tax)
                  )}
                </strong>
              </div>
              {useWalletBalance && (walletQuote?.walletAmountUsed ?? 0) > 0 ? (
                <div className="summary-wallet-deduction">
                  <span>Wallet used</span>
                  <strong>-{formatCurrency(walletQuote?.walletAmountUsed ?? 0)}</strong>
                </div>
              ) : null}
              {couponPreviewDiscount > 0 ? (
                <div className="summary-saving-strip">
                  You're Saving {formatCurrency(couponPreviewDiscount)}
                </div>
              ) : null}
            </section>
            </div>
            <div className="checkout-form__footer">
              <div className="checkout-form__footer-total">
                <span>Total Amount</span>
                <strong>{formatCurrency(walletQuote?.finalPayableAmount ?? total)}</strong>
              </div>
              {orderAnimationActive ? (
                <div className="checkout-form__success-box">
                  <span>Order placed</span>
                </div>
              ) : (
              <button
                ref={placeOrderButtonRef}
                className={`button checkout-form__submit-button ${submittingOrder ? "is-loading" : ""}`}
                type="submit"
                disabled={submittingOrder}
              >
                {submittingOrder ? (
                  <>
                    <span className="checkout-form__submit-truck" aria-hidden="true">
                      <span className="checkout-form__submit-truck-body" />
                      <span className="checkout-form__submit-truck-cabin" />
                      <span className="checkout-form__submit-truck-cargo" />
                      <span className="checkout-form__submit-truck-wheel checkout-form__submit-truck-wheel--left" />
                      <span className="checkout-form__submit-truck-wheel checkout-form__submit-truck-wheel--right" />
                    </span>
                    <span>Placing order...</span>
                  </>
                ) : (
                  `Confirm & Place Order ${formatCurrency(walletQuote?.finalPayableAmount ?? total)}`
                )}
              </button>
              )}
            </div>
          </form>
        </div>
      )}
      {deliveryWarningOpen ? (
        <div
          className="checkout-wallet-modal checkout-delivery-warning-modal"
          role="presentation"
        >
          <div
            className="checkout-wallet-modal__card checkout-delivery-warning-modal__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-delivery-warning-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="checkout-wallet-modal__header">
              <div>
                <span className="eyebrow">Delivery unavailable</span>
                <h2 id="checkout-delivery-warning-title">This pincode is not serviceable</h2>
              </div>
              <button type="button" className="checkout-wallet-modal__close" onClick={closeDeliveryWarning}>
                ×
              </button>
            </div>
            <p className="checkout-wallet-modal__note">
              {deliveryWarningMessage || "Home delivery is not available for the selected pincode."}
            </p>
            <div className="checkout-wallet-modal__actions checkout-delivery-warning-modal__actions">
              <Link
                className="button button--light"
                to="/address"
                state={{ openLocationPickerFirst: true }}
                onClick={closeDeliveryWarning}
              >
                Select another address
              </Link>
              <button type="button" className="button" onClick={closeDeliveryWarning}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {walletDetailsOpen ? (
        <div className="checkout-wallet-modal" role="presentation">
          <div
            className="checkout-wallet-modal__card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-wallet-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="checkout-wallet-modal__header">
              <div>
                <span className="eyebrow">Wallet details</span>
                <h2 id="checkout-wallet-modal-title">Use wallet balance</h2>
              </div>
              <button type="button" className="checkout-wallet-modal__close" onClick={() => setWalletDetailsOpen(false)}>
                ×
              </button>
            </div>
            <div className="checkout-wallet-modal__summary">
              <div>
                <span>Wallet balance</span>
                <strong>{formatCurrency(walletBalance)}</strong>
              </div>
              <div>
                <span>Maximum usable</span>
                <strong>{formatCurrency(walletQuote?.maxWalletUsable ?? 0)}</strong>
              </div>
              <div>
                <span>Selected amount</span>
                <strong>{formatCurrency(walletQuote?.walletAmountUsed ?? 0)}</strong>
              </div>
              <div>
                <span>Remaining payable</span>
                <strong>{formatCurrency(walletQuote?.finalPayableAmount ?? total)}</strong>
              </div>
            </div>
            {walletSettings?.enableWalletUsage ? (
              <div className="checkout-wallet-modal__actions">
                <button
                  type="button"
                  className={`button ${useWalletBalance ? "button--ghost" : ""}`}
                  onClick={() => void handleUseWallet()}
                  disabled={walletApplyLoading || submittingOrder}
                >
                  {walletApplyLoading ? (
                    <span className="button-loading">
                      <span className="button-loading__spinner" aria-hidden="true" />
                      Applying...
                    </span>
                  ) : (
                    "Use wallet"
                  )}
                </button>
                <button
                  type="button"
                  className={`button ${!useWalletBalance ? "button--ghost" : ""}`}
                  onClick={handleDontUseWallet}
                  disabled={walletApplyLoading || submittingOrder}
                >
                  Don&apos;t use wallet
                </button>
              </div>
            ) : (
              <p className="checkout-wallet-modal__note">Wallet usage is disabled by admin.</p>
            )}
            {useWalletBalance && walletQuote?.reason ? (
              <p className="checkout-wallet-modal__note">{walletQuote.reason}</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default CheckoutPage;
